// src/components/courses/CourseList.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  BookOpenIcon, 
  PlusIcon
} from '@heroicons/react/24/outline';
import useAuthStore from '@/stores/authStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import toast from 'react-hot-toast';
import { 
  getMyCourses, 
  createCourse, 
  deleteCourse, 
  getCourseStudents, 
  getCourseTeachers,
  getCourseSessions 
} from '@/services/api';
import CourseCard from './CourseCard';
import AddCourseModal from '../modals/AddCourseModal';
import DeleteCourseModal from '../modals/DeleteCourseModal';

// Create a reference to toast functions to prevent minification issues
const toastNotify = {
  success: (message, options) => toast.success(message, options),
  error: (message, options) => toast.error(message, options),
  info: (message, options) => toast(message, { ...options, icon: 'ℹ️' })
};

const CourseList = ({ onCourseSelect, user, userRole, institutionName, selectedInstitution: selectedInstitutionProp }) => {
  const { t } = useTranslation();
  
  // Set page title
  useEffect(() => {
    document.title = `${institutionName || t('courses.defaultInstitution')} - ${t('navigation.courses')}`;
  }, [institutionName, t]);

  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // ADD WEBSOCKET HOOK
  const { addMessageHandler, isConnected } = useWebSocket();

  // ADD REF TO TRACK COURSES FOR WEBSOCKET HANDLERS
  const coursesRef = useRef([]);
  useEffect(() => {
    coursesRef.current = courses;
  }, [courses]);

  // Modal states
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  // Form states
  const [formData, setFormData] = useState({ name: '' });

  // Loading/Error states for operations
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const [addCourseError, setAddCourseError] = useState('');
  const [isDeletingCourse, setIsDeletingCourse] = useState(false);
  const [deleteCourseError, setDeleteCourseError] = useState('');

  // ✅ FIX: Use selectedInstitution from prop (passed by App.jsx) instead of independently reading localStorage
  // Fall back to localStorage only if prop is not available (e.g., during initial render)
  const [selectedInstitution, setSelectedInstitution] = useState(selectedInstitutionProp || null);

  // Keep in sync with prop changes
  useEffect(() => {
    if (selectedInstitutionProp?.id) {
      setSelectedInstitution(selectedInstitutionProp);
    } else {
      // Fallback: try localStorage if prop is not ready yet
      const stored = localStorage.getItem('selectedInstitution');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setSelectedInstitution(parsed);
          console.log('Institution loaded from localStorage fallback:', parsed);
        } catch (e) {
          console.error('Failed to parse institution:', e);
          setSelectedInstitution({});
        }
      } else {
        console.warn('No selectedInstitution available from prop or localStorage');
        setSelectedInstitution({});
      }
    }
  }, [selectedInstitutionProp]);

  // Use the onCourseSelect prop if provided, otherwise use navigation
  const handleCourseSelect = useCallback((courseId, courseName) => {
    if (onCourseSelect) {
      onCourseSelect(courseId, courseName);
    } else {
      navigate(`/courses/${courseId}/upcoming`, { state: { courseName } });
    }
  }, [onCourseSelect, navigate]);

  // Enrich courses with additional data
  const enrichCoursesWithCounts = useCallback(async (coursesData) => {
    const enrichedCourses = await Promise.all(
      coursesData.map(async (course) => {
        let enrichedCourse = {
          ...course,
          completedSessionsCount: 0,
          upcomingSessionsCount: 0,
          nextSessionStartTime: null,
          studentCount: 0,
          isOwner: false
        };

        try {
          const sessionsResponse = await getCourseSessions(course.id);
          
          if (sessionsResponse.success) {
            const sessions = sessionsResponse.data || [];
            const now = new Date();
            const completedSessions = sessions.filter(s => new Date(s.end_time) < now);
            const upcomingSessions = sessions.filter(s => new Date(s.end_time) >= now);
            const nextSession = upcomingSessions
              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];

            enrichedCourse.completedSessionsCount = completedSessions.length;
            enrichedCourse.upcomingSessionsCount = upcomingSessions.length;
            enrichedCourse.nextSessionStartTime = nextSession?.start_time || null;
          }
        } catch (err) {
          if (err.statusCode === 403) {
            console.log(`User doesn't have permission to view sessions for course ${course.id}`);
          } else {
            console.error(`Error fetching sessions for course ${course.id}:`, err);
          }
        }

        // For teachers, try to get additional info
        if (userRole === 'teacher') {
          try {
            const studentsResponse = await getCourseStudents(course.id);
            if (studentsResponse.success) {
              enrichedCourse.studentCount = studentsResponse.data.length;
            }
          } catch (err) {
            if (err.statusCode === 403) {
              console.log(`User doesn't have permission to view students for course ${course.id}`);
            } else {
              console.error(`Error fetching students for course ${course.id}:`, err);
            }
          }

          try {
            const teachersResponse = await getCourseTeachers(course.id);
            if (teachersResponse.success) {
              const currentTeacher = (teachersResponse.data || []).find(
                t => t.user_id === user?.id || t.user_id === user?.userId
              );
              enrichedCourse.isOwner = currentTeacher?.role === 'owner';
            }
          } catch (err) {
            if (err.statusCode === 403) {
              console.log(`User doesn't have permission to view teachers for course ${course.id}`);
            } else {
              console.error(`Error fetching teachers for course ${course.id}:`, err);
            }
          }
        }

        return enrichedCourse;
      })
    );

    return enrichedCourses;
  }, [userRole, user]);

  // Load courses
  const loadCourses = useCallback(async (isWebSocketRefresh = false) => {
    if (!isWebSocketRefresh) {
      setIsLoading(true);
    }
    setError('');
    try {
      const response = await getMyCourses();
      if (response.success) {
        const allCourses = response.data || [];
        const enrichedCourses = await enrichCoursesWithCounts(allCourses);
        setCourses(enrichedCourses);
      } else {
        setError(response.message || t('courses.errors.loadFailed'));
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err.message || t('courses.errors.loadError'));
    } finally {
      if (!isWebSocketRefresh) {
        setIsLoading(false);
      }
    }
  }, [enrichCoursesWithCounts, t]);

  // Load courses on mount
  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  // Track if we just created a course to prevent reload
  const justCreatedCourseRef = useRef(null);
  
  // Store refs for values that handlers need
  const userRef = useRef(user);
  const userRoleRef = useRef(userRole);
  const selectedInstitutionRef = useRef(selectedInstitution);
  const loadCoursesRef = useRef(loadCourses);
  const tRef = useRef(t);
  
  // Update refs when values change
  useEffect(() => {
    userRef.current = user;
    userRoleRef.current = userRole;
    selectedInstitutionRef.current = selectedInstitution;
    loadCoursesRef.current = loadCourses;
    tRef.current = t;
  }, [user, userRole, selectedInstitution, loadCourses, t]);

  // WEBSOCKET EVENT HANDLERS
  useEffect(() => {
    if (!isConnected || !addMessageHandler) return;

    // Handler for course updates
    const handleCourseUpdate = (data) => {
      console.log('📚 Course update received:', data);
      
      if (data.action === 'created' && data.course) {
        if (userRoleRef.current === 'student') {
          return;
        } else if (userRoleRef.current === 'teacher') {
          const courseExists = coursesRef.current.some(c => c.id === data.course.id);
          if (!courseExists) {
            if (justCreatedCourseRef.current === data.course.name) {
              justCreatedCourseRef.current = null;
              window.setTimeout(() => {
                toastNotify.success(tRef.current('courses.toast.courseAdded', { name: data.course.name }), {
                  duration: 4000,
                  icon: '📚'
                });
              }, 100);
              
              loadCoursesRef.current(true);
              return;
            }
            
            const newCourse = {
              ...data.course,
              completedSessionsCount: 0,
              upcomingSessionsCount: 0,
              nextSessionStartTime: null,
              studentCount: 0,
              isOwner: false
            };
            
            setCourses(prev => [...prev, newCourse]);
            window.setTimeout(() => {
              toastNotify.success(tRef.current('courses.toast.courseAdded', { name: data.course.name }), {
                duration: 4000,
                icon: '📚'
              });
            }, 100);
          }
        }
      } else if (data.action === 'updated' && data.course) {
        setCourses(prev => prev.map(c => 
          c.id === data.course.id 
            ? { ...c, name: data.course.name, description: data.course.description }
            : c
        ));
        window.setTimeout(() => {
          toastNotify.info(tRef.current('courses.toast.courseUpdated', { name: data.course.name }), {
            duration: 3000
          });
        }, 100);
      } else if (data.action === 'deleted') {
        const courseId = data.course?.id || data.courseId;
        const courseName = data.course?.name;
        
        const existingCourse = coursesRef.current.find(c => c.id === courseId);
        const finalCourseName = courseName || existingCourse?.name;
        
        if (finalCourseName) {
          window.setTimeout(() => {
            toastNotify.error(tRef.current('courses.toast.courseDeleted', { name: finalCourseName }), {
              duration: 4000
            });
          }, 100);
        }
        
        setCourses(prev => prev.filter(c => c.id !== courseId));
      }
    };

    // Handler for enrollment updates
    const handleEnrollmentUpdate = (data) => {
      console.log('🎓 Enrollment update received:', data);
      
      const currentUserId = userRef.current?.id || userRef.current?.userId;
      
      if (data.action === 'enrolled') {
        if (userRoleRef.current === 'student') {
          if (data.studentUserId === currentUserId) {
            const courseExists = coursesRef.current.some(c => c.id === data.courseId);
            if (!courseExists) {
              const newCourse = {
                id: data.courseId,
                name: data.courseName || data.course?.name || tRef.current('courses.newCourse'),
                description: data.course?.description || '',
                institution_id: selectedInstitutionRef.current?.id,
                ...(data.course || {}),
                completedSessionsCount: 0,
                upcomingSessionsCount: 0,
                nextSessionStartTime: null,
                studentCount: 0,
                isOwner: false
              };
              
              setCourses(prev => [...prev, newCourse]);
              
              if (data.courseId) {
                (async () => {
                  try {
                    const sessionsResponse = await getCourseSessions(data.courseId);
                    if (sessionsResponse.success) {
                      const sessions = sessionsResponse.data || [];
                      const now = new Date();
                      const completedSessions = sessions.filter(s => new Date(s.end_time) < now);
                      const upcomingSessions = sessions.filter(s => new Date(s.end_time) >= now);
                      const nextSession = upcomingSessions
                        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
                      
                      setCourses(prev => prev.map(c => 
                        c.id === data.courseId 
                          ? {
                              ...c,
                              completedSessionsCount: completedSessions.length,
                              upcomingSessionsCount: upcomingSessions.length,
                              nextSessionStartTime: nextSession?.start_time || null
                            }
                          : c
                      ));
                    }
                  } catch (err) {
                    console.log('Error enriching course data:', err);
                  }
                })();
              }
              
              window.setTimeout(() => {
                toastNotify.success(tRef.current('courses.toast.enrolled', { name: newCourse.name }), {
                  duration: 5000,
                  icon: '🎉'
                });
              }, 100);
            }
          }
        } else if (userRoleRef.current === 'teacher') {
          const course = coursesRef.current.find(c => c.id === data.courseId);
          if (course) {
            setCourses(prev => prev.map(c => 
              c.id === data.courseId 
                ? { ...c, studentCount: (c.studentCount || 0) + 1 }
                : c
            ));
            window.setTimeout(() => {
              toastNotify.info(
                tRef.current('courses.toast.studentEnrolled', { 
                  studentName: data.studentName || tRef.current('courses.aStudent'),
                  courseName: data.courseName || course.name 
                }), 
                {
                  duration: 3000,
                  icon: '👥'
                }
              );
            }, 100);
          }
        }
      } else if (data.action === 'unenrolled') {
        if (userRoleRef.current === 'student') {
          if (data.studentUserId === currentUserId) {
            const courseToRemove = coursesRef.current.find(c => c.id === data.courseId);
            const courseName = data.courseName || courseToRemove?.name || tRef.current('courses.theCourse');
            
            setCourses(prev => prev.filter(c => c.id !== data.courseId));
            
            window.setTimeout(() => {
              toastNotify.info(tRef.current('courses.toast.unenrolled', { name: courseName }), {
                duration: 4000
              });
            }, 100);
          }
        } else if (userRoleRef.current === 'teacher') {
          const course = coursesRef.current.find(c => c.id === data.courseId);
          if (course) {
            setCourses(prev => prev.map(c => 
              c.id === data.courseId 
                ? { ...c, studentCount: Math.max(0, (c.studentCount || 0) - 1) }
                : c
            ));
            window.setTimeout(() => {
              toastNotify.info(
                tRef.current('courses.toast.studentRemoved', { 
                  studentName: data.studentName || tRef.current('courses.aStudent'),
                  courseName: data.courseName || course.name 
                }), 
                {
                  duration: 3000
                }
              );
            }, 100);
          }
        }
      } else if (data.action === 'class_enrolled') {
        if (userRoleRef.current === 'student') {
          loadCoursesRef.current(true);
        } else if (userRoleRef.current === 'teacher' && data.enrolledCount > 0) {
          const course = coursesRef.current.find(c => c.id === data.courseId);
          if (course) {
            setCourses(prev => prev.map(c => 
              c.id === data.courseId 
                ? { ...c, studentCount: (c.studentCount || 0) + data.enrolledCount }
                : c
            ));
          }
          window.setTimeout(() => {
            toastNotify.info(
              tRef.current('courses.toast.bulkEnrolled', { 
                count: data.enrolledCount,
                courseName: data.courseName || tRef.current('courses.course')
              }), 
              {
                duration: 4000,
                icon: '👥'
              }
            );
          }, 100);
        }
      }
    };

    // Handler for session updates
    const handleSessionUpdate = (data) => {
      console.log('📅 Session update received:', data);
      
      const courseId = data.courseId || data.course_id;
      const session = data.session || data;
      
      if (courseId) {
        const courseExists = coursesRef.current.some(c => c.id === courseId);
        
        if (courseExists) {
          if (data.action === 'created' || data.action === 'scheduled') {
            const now = new Date();
            const sessionEnd = new Date(session.end_time || session.endTime);
            
            setCourses(prev => prev.map(course => {
              if (course.id === courseId) {
                let upcomingCount = course.upcomingSessionsCount || 0;
                let completedCount = course.completedSessionsCount || 0;
                
                if (sessionEnd >= now) {
                  upcomingCount += 1;
                } else {
                  completedCount += 1;
                }
                
                const sessionStart = session.start_time || session.startTime;
                let nextSessionStartTime = course.nextSessionStartTime;
                if (!nextSessionStartTime || new Date(sessionStart) < new Date(nextSessionStartTime)) {
                  nextSessionStartTime = sessionStart;
                }
                
                return {
                  ...course,
                  upcomingSessionsCount: upcomingCount,
                  completedSessionsCount: completedCount,
                  nextSessionStartTime
                };
              }
              return course;
            }));
            
            if (sessionEnd >= now) {
              window.setTimeout(() => {
                toastNotify.success(
                  tRef.current('courses.toast.sessionScheduled', { 
                    title: session.title || tRef.current('courses.session')
                  }), 
                  {
                    duration: 3000,
                    icon: '📅'
                  }
                );
              }, 100);
            }
          } else if (data.action === 'deleted' || data.action === 'updated' || data.action === 'cancelled') {
            loadCoursesRef.current(true);
          }
        }
      }
    };

    // Handler for course member updates
    const handleCourseMemberUpdate = (data) => {
      console.log('👥 Course member update received:', data);
      
      const currentUserId = userRef.current?.id || userRef.current?.userId;
      
      if (data.courseId) {
        if (data.action === 'student_added' && userRoleRef.current === 'student' && data.studentUserId === currentUserId) {
          loadCoursesRef.current(true);
          return;
        }
        
        if (data.action === 'student_removed' && userRoleRef.current === 'student' && data.studentUserId === currentUserId) {
          const courseToRemove = coursesRef.current.find(c => c.id === data.courseId);
          if (courseToRemove) {
            setCourses(prev => prev.filter(c => c.id !== data.courseId));
            window.setTimeout(() => {
              toastNotify.info(tRef.current('courses.toast.removedFrom', { name: courseToRemove.name }), {
                duration: 4000
              });
            }, 100);
          }
          return;
        }
        
        const courseExists = coursesRef.current.some(c => c.id === data.courseId);
        
        if (courseExists) {
          setCourses(prev => prev.map(course => {
            if (course.id === data.courseId) {
              let newStudentCount = course.studentCount || 0;
              
              if (data.action === 'student_added') {
                newStudentCount = data.studentCount !== undefined ? data.studentCount : newStudentCount + 1;
                if (userRoleRef.current === 'teacher' && data.studentName) {
                  window.setTimeout(() => {
                    toastNotify.info(
                      tRef.current('courses.toast.studentJoined', { 
                        studentName: data.studentName,
                        courseName: course.name 
                      }), 
                      {
                        duration: 3000
                      }
                    );
                  }, 100);
                }
              } else if (data.action === 'student_removed') {
                newStudentCount = data.studentCount !== undefined ? data.studentCount : Math.max(0, newStudentCount - 1);
                if (userRoleRef.current === 'teacher' && data.studentName) {
                  window.setTimeout(() => {
                    toastNotify.info(
                      tRef.current('courses.toast.studentLeft', { 
                        studentName: data.studentName,
                        courseName: course.name 
                      }), 
                      {
                        duration: 3000
                      }
                    );
                  }, 100);
                }
              } else if (data.action === 'students_bulk_added') {
                newStudentCount = data.studentCount !== undefined ? data.studentCount : newStudentCount + (data.addedCount || 0);
                if (userRoleRef.current === 'teacher' && data.addedCount > 0) {
                  window.setTimeout(() => {
                    toastNotify.info(
                      tRef.current('courses.toast.studentsAdded', { 
                        count: data.addedCount,
                        courseName: course.name 
                      }), 
                      {
                        duration: 3000,
                        icon: '👥'
                      }
                    );
                  }, 100);
                }
              } else if (data.action === 'teacher_added') {
                if (data.teacherName) {
                  window.setTimeout(() => {
                    toastNotify.info(
                      tRef.current('courses.toast.teacherAdded', { 
                        teacherName: data.teacherName,
                        courseName: course.name 
                      }), 
                      {
                        duration: 3000,
                        icon: '👨‍🏫'
                      }
                    );
                  }, 100);
                }
                return course;
              } else if (data.action === 'teacher_removed') {
                if (data.teacherName) {
                  window.setTimeout(() => {
                    toastNotify.info(
                      tRef.current('courses.toast.teacherRemoved', { courseName: course.name }), 
                      {
                        duration: 3000
                      }
                    );
                  }, 100);
                }
                return course;
              }
              
              return {
                ...course,
                studentCount: newStudentCount
              };
            }
            return course;
          }));
        }
      }
    };

    // Register WebSocket handlers
    const unsubscribeCourseUpdate = addMessageHandler('course-update', handleCourseUpdate);
    const unsubscribeEnrollmentUpdate = addMessageHandler('enrollment-update', handleEnrollmentUpdate);
    const unsubscribeSessionUpdate1 = addMessageHandler('session-update', handleSessionUpdate);
    const unsubscribeSessionUpdate2 = addMessageHandler('session_update', handleSessionUpdate);
    const unsubscribeCourseMemberUpdate = addMessageHandler('course-member-update', handleCourseMemberUpdate);

    // Cleanup
    return () => {
      unsubscribeCourseUpdate();
      unsubscribeEnrollmentUpdate();
      unsubscribeSessionUpdate1();
      unsubscribeSessionUpdate2();
      unsubscribeCourseMemberUpdate();
    };
  }, [isConnected, addMessageHandler]);

  // Handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCourseSubmit = async (e) => {
    e.preventDefault();
    setIsAddingCourse(true);
    setAddCourseError('');

    // ✅ FIX 3: Defensive check - try multiple sources for institution ID
    let institution = selectedInstitution;
    if (!institution?.id) {
      console.warn('Institution not in state, re-reading from localStorage...');
      const stored = localStorage.getItem('selectedInstitution');
      if (stored) {
        try {
          institution = JSON.parse(stored);
          console.log('Institution re-read from localStorage:', institution);
        } catch (e) {
          console.error('Failed to parse institution from localStorage:', e);
        }
      }
    }
    
    // Last resort: try currentInstitutionId (used by the API layer for X-Institution-Id header)
    if (!institution?.id) {
      const fallbackId = localStorage.getItem('currentInstitutionId');
      if (fallbackId) {
        console.warn('Using currentInstitutionId as last resort:', fallbackId);
        institution = { id: fallbackId, name: institutionName || 'Unknown' };
      }
    }

    // Final validation
    if (!institution?.id) {
      console.error('No institution ID available after all checks');
      setAddCourseError(t('courses.errors.noInstitution'));
      setIsAddingCourse(false);
      return;
    }

    try {
      const courseData = {
        name: formData.name.trim(),
        institutionId: institution.id
      };

      console.log('Creating course with data:', courseData);
      justCreatedCourseRef.current = courseData.name;

      const response = await createCourse(courseData);
      if (response.success) {
        setShowAddCourseModal(false);
        setFormData({ name: '' });
      } else {
        justCreatedCourseRef.current = null;
        setAddCourseError(response.message || t('courses.errors.createFailed'));
      }
    } catch (err) {
      justCreatedCourseRef.current = null;
      console.error('Error creating course:', err);
      setAddCourseError(err.message || t('courses.errors.createError'));
    } finally {
      setIsAddingCourse(false);
    }
  };

  const handleDeleteCourse = (course) => {
    setCourseToDelete(course);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;

    setIsDeletingCourse(true);
    setDeleteCourseError('');

    try {
      const response = await deleteCourse(courseToDelete.id);
      if (response.success) {
        setShowDeleteConfirmModal(false);
        setCourseToDelete(null);
      } else {
        setDeleteCourseError(response.message || t('courses.errors.deleteFailed'));
      }
    } catch (err) {
      setDeleteCourseError(err.message || t('courses.errors.deleteError'));
    } finally {
      setIsDeletingCourse(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full mx-auto px-4 sm:px-6 py-8 sm:max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="w-full mx-auto px-4 sm:px-6 py-4 sm:py-8 sm:max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">{t('courses.title')}</h2>
            <p className="text-slate-600 mt-1">
              {userRole === 'teacher' ? t('courses.subtitle.teacher') : t('courses.subtitle.student')}
            </p>
          </div>
          {userRole === 'teacher' && (
            <button
              onClick={() => setShowAddCourseModal(true)}
              className="mt-4 sm:mt-0 flex items-center px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white font-medium rounded-xl hover:from-cyan-700 hover:to-cyan-600 transition-all duration-200 shadow-sm"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              {t('courses.addCourse')}
            </button>
          )}
        </div>

        {/* Connection status warning */}
        {!isConnected && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              ⚠️ {t('courses.disconnectedWarning')}
            </p>
          </div>
        )}

        {courses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-slate-200">
            <BookOpenIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">{t('courses.empty.title')}</h3>
            <p className="text-slate-500 mb-6">
              {userRole === 'teacher'
                ? selectedInstitution?.id
                  ? t('courses.empty.teacher', { institution: selectedInstitution.name })
                  : t('courses.empty.noInstitution')
                : t('courses.empty.student')}
            </p>
            {userRole === 'teacher' && selectedInstitution?.id && (
              <button
                onClick={() => setShowAddCourseModal(true)}
                className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                {t('courses.createCourse')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {courses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                userRole={userRole}
                onCourseSelect={handleCourseSelect}
                onDeleteCourse={handleDeleteCourse}
              />
            ))}
          </div>
        )}

        {/* Add Course Modal */}
        {userRole === 'teacher' && (
          <AddCourseModal
            isOpen={showAddCourseModal}
            onClose={() => {
              setShowAddCourseModal(false);
              setFormData({ name: '' });
              setAddCourseError('');
            }}
            onSubmit={handleAddCourseSubmit}
            isSubmitting={isAddingCourse}
            error={addCourseError}
            formData={formData}
            onChange={handleFormChange}
          />
        )}

        {/* Delete Confirmation Modal */}
        {userRole === 'teacher' && courseToDelete && (
          <DeleteCourseModal
            isOpen={showDeleteConfirmModal}
            onClose={() => {
              setShowDeleteConfirmModal(false);
              setCourseToDelete(null);
              setDeleteCourseError('');
            }}
            onConfirm={confirmDeleteCourse}
            courseName={courseToDelete.name}
            isDeleting={isDeletingCourse}
            error={deleteCourseError}
          />
        )}
      </div>
    </div>
  );
};

export default CourseList;